import { useState } from "react";
import { BookOpenCheck, Clock3, Layers, Repeat2 } from "lucide-react";
import { BottomSheet } from "./bottom-sheet";
import { RippleButton } from "./ripple-button";
import { REVISION_LEGEND } from "@/lib/revision-labels";
import { cn } from "@/lib/utils";

export function RevisionInfoSheet({
  open,
  onClose,
  showDontAskAgain,
  onDontAskAgain,
}: {
  open: boolean;
  onClose: () => void;
  /** Only shown for the automatic first-run explainer. */
  showDontAskAgain?: boolean;
  onDontAskAgain?: (value: boolean) => void;
}) {
  const [dontShow, setDontShow] = useState(true);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="How Revision Works"
      subtitle="Every chip tells you which lecture you're revising and which round it is."
      footer={
        <RippleButton
          className="w-full"
          onClick={() => {
            if (showDontAskAgain) onDontAskAgain?.(dontShow);
            onClose();
          }}
        >
          Got it
        </RippleButton>
      }
    >
      <div className="space-y-4 pb-2">
        <Section icon={Layers} title="Lecture levels">
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            <b className="text-foreground">L1</b> = Lecture 1, <b className="text-foreground">L2</b>{" "}
            = Lecture 2, <b className="text-foreground">L3</b> = Lecture 3 … up to{" "}
            <b className="text-foreground">L10</b> = Lecture 10.
          </p>
        </Section>

        <Section icon={Repeat2} title="Revision rounds">
          <ul className="space-y-1.5 text-[13px] text-muted-foreground">
            <li>
              <b className="text-foreground">R1</b> — First revision
            </li>
            <li>
              <b className="text-foreground">R2</b> — Second revision
            </li>
            <li>
              <b className="text-foreground">R3</b> — Third revision
            </li>
            <li>
              <b className="text-foreground">R4</b> — Final reinforcement revision
            </li>
          </ul>
        </Section>

        <Section icon={Clock3} title="Carried">
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            If a revision is missed on its scheduled day, it is automatically carried forward to the
            next day until you complete it — nothing is ever lost.
          </p>
        </Section>

        <Section icon={BookOpenCheck} title="Examples">
          <div className="space-y-2 text-[13px]">
            <p>
              <span className="rounded-full border border-hairline bg-surface px-2.5 py-1 font-semibold">
                L3 • R2
              </span>
              <span className="ml-2 text-muted-foreground">Lecture 3 → Second revision</span>
            </p>
            <p>
              <span className="rounded-full border border-hairline bg-surface px-2.5 py-1 font-semibold">
                L8 • R4
              </span>
              <span className="ml-2 text-muted-foreground">
                Lecture 8 → Fourth &amp; final revision
              </span>
            </p>
          </div>
        </Section>

        <div className="rounded-[20px] border border-hairline bg-surface p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Colour guide
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[12.5px]">
            {REVISION_LEGEND.map((item) => (
              <span key={item.label} className="flex items-center gap-2">
                <span className={cn("size-2.5 rounded-full", item.dot)} />
                {item.label}
              </span>
            ))}
          </div>
        </div>

        {showDontAskAgain ? (
          <button
            type="button"
            onClick={() => setDontShow((v) => !v)}
            aria-pressed={dontShow}
            className="flex w-full items-center gap-3 rounded-2xl border border-hairline bg-surface px-4 py-3 text-left"
          >
            <span
              className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-md border text-[11px] font-bold",
                dontShow
                  ? "border-transparent text-primary-foreground [background-image:var(--gradient-brand)]"
                  : "border-hairline",
              )}
            >
              {dontShow ? "✓" : ""}
            </span>
            <span className="text-[13px]">Don&apos;t show again</span>
          </button>
        ) : null}
      </div>
    </BottomSheet>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Layers;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[20px] border border-hairline bg-surface p-4">
      <p className="flex items-center gap-2 text-[12px] font-bold tracking-tight">
        <Icon className="size-4 text-primary" /> {title}
      </p>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}